import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, Filter } from 'lucide-react';
import api from '../utils/api';

const eventColors = {
  auth_session:        'bg-green-500/20 text-green-400',
  bot_start:           'bg-green-500/20 text-green-400',
  new_user:            'bg-cyan-500/20 text-cyan-400',
  order_created:       'bg-blue-500/20 text-blue-400',
  deposit_created:     'bg-blue-500/20 text-blue-400',
  order_paid:          'bg-amber-500/20 text-amber-400',
  order_status_update: 'bg-blue-500/20 text-blue-400',
  kyc_started:         'bg-purple-500/20 text-purple-400',
  kyc_confirm:         'bg-purple-500/20 text-purple-400',
  verification_check:  'bg-purple-500/20 text-purple-400',
};

const DEFAULT_COLOR = 'bg-gray-500/20 text-gray-400';

export default function EventsScreen() {
  const [events, setEvents] = useState([]);
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventName, setEventName] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [limit, setLimit] = useState(100);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEvents({
        eventName: eventName || undefined,
        limit,
      });
      setEvents(data.events || []);
      if (data.labels) setLabels(data.labels);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [eventName, limit]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filteredEvents = searchFilter
    ? events.filter(e => {
        const q = searchFilter.toLowerCase();
        return (
          (e.username && e.username.toLowerCase().includes(q)) ||
          (e.fullName && e.fullName.toLowerCase().includes(q)) ||
          (e.userId && String(e.userId).includes(q))
        );
      })
    : events;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch { return dateStr; }
  };

  const HIDDEN_DATA_KEYS = new Set([
    'username', 'fullName', 'full_name', 'user_id', 'userId', 'ip',
  ]);

  const formatData = (raw) => {
    if (!raw) return '';
    try {
      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const entries = Object.entries(obj).filter(([k]) => !HIDDEN_DATA_KEYS.has(k));
      if (entries.length === 0) return '';
      return entries.map(([k, v]) => `${k}: ${v}`).join(', ');
    } catch {
      return String(raw);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">События</h2>
        <button
          onClick={loadEvents}
          disabled={loading}
          className="p-2 bg-gray-800 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[150px]">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <select
            value={eventName}
            onChange={e => setEventName(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-purple-500 appearance-none"
          >
            <option value="">Все события</option>
            {Object.entries(labels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
        <div className="relative flex-1 min-w-[150px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Имя, @username или ID..."
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <select
          value={limit}
          onChange={e => setLimit(Number(e.target.value))}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
        >
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
          <option value={500}>500</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Events list */}
      <div className="bg-gray-800 rounded-xl border border-gray-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-700">
                <th className="text-left p-3">Время</th>
                <th className="text-left p-3">Пользователь</th>
                <th className="text-left p-3">Событие</th>
                <th className="text-left p-3">IP</th>
                <th className="text-left p-3">Данные</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-t border-gray-700/50">
                    <td colSpan={5} className="p-3">
                      <div className="h-4 bg-gray-700 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-gray-500">
                    Нет событий
                  </td>
                </tr>
              ) : (
                filteredEvents.map((event, i) => (
                  <tr key={i} className="border-t border-gray-700/50 hover:bg-gray-700/30">
                    <td className="p-3 text-gray-400 text-xs whitespace-nowrap">
                      {formatDate(event.time)}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      {(event.fullName || event.username) ? (
                        <>
                          <div className="text-white text-xs">{event.fullName || `@${event.username}`}</div>
                          {event.fullName && event.username && (
                            <div className="text-gray-500 text-xs">@{event.username}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${eventColors[event.event] || DEFAULT_COLOR}`}>
                        {event.label || event.event || '—'}
                      </span>
                    </td>
                    <td className="p-3 text-gray-400 text-xs font-mono whitespace-nowrap">
                      {event.ip || ''}
                    </td>
                    <td className="p-3 text-gray-300 text-xs max-w-[200px] truncate">
                      {formatData(event.data) || <span className="text-gray-600">—</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && (
        <p className="text-xs text-gray-500 mt-3">
          Показано: {filteredEvents.length} из {events.length} событий
        </p>
      )}
    </div>
  );
}
